import { type FC, useMemo } from 'react';
import {
  DateLabel,
  DateLabelContainer,
  IconContainer,
  MainTrainingCard,
  ProgressBar,
  ProgressBarContainer,
  ProgressFill,
  ProgressText,
  Row,
  Title,
  TitleContainer,
  TrainingPrimaryButton,
} from '../TrainingCard';
import { useTheme } from 'styled-components';
import { NamedContentEntryText } from '../../../ContentEntryText/NamedContentEntryText';
import { useLanguageContext } from '../../../../lib/hooks/useLanguageContext';
import { Icon, IconType } from '../../../Props/Icon/Icon';
import { CardStatus } from '../../enums/CardStatus';
import { getCardStatus } from '../../utils/getCardStatus';
import type { EnrolledCampaignListResponse } from '../../../../apiHooks/useCampaignsQuery';

type MandatoryTrainingCardProps = {
  campaign: EnrolledCampaignListResponse;
};

export const MandatoryTrainingCard: FC<MandatoryTrainingCardProps> = ({ campaign }: MandatoryTrainingCardProps) => {
  const { dueDate, campaignUrl, name, modulesProgress, modulesTotal, learnerStatus } = campaign;
  const dd = useMemo(() => new Date(dueDate), [dueDate]);
  const cardStatus: CardStatus = getCardStatus(dd);
  const progress = modulesTotal > 0 ? (modulesProgress / modulesTotal) * 100 : 0;
  const dateText =
    cardStatus === CardStatus.urgent ? (
      <NamedContentEntryText container="span" refKey="yourTraining" subKey="urgent" />
    ) : (
      <NamedContentEntryText container="span" refKey="yourTraining" subKey="dueDate" />
    );
  const buttonText =
    learnerStatus === 'inProgress' ? (
      <NamedContentEntryText container="span" refKey="yourTraining" subKey="resume" />
    ) : (
      <NamedContentEntryText container="span" refKey="yourTraining" subKey="startNow" />
    );
  const [langCode] = useLanguageContext();
  const theme = useTheme();

  return (
    <MainTrainingCard cardStatus={cardStatus}>
      <Row>
        <TitleContainer>
          <IconContainer>
            <Icon icon={IconType.GOAL_FLAG} width={24} height={24} className="rtl-flip" />
          </IconContainer>
          <Title>{name}</Title>
        </TitleContainer>
        <DateLabelContainer cardStatus={cardStatus}>
          <Icon width={16} height={16} icon={IconType.CALENDAR} />
          <DateLabel>
            {dateText}: {dd.toLocaleString(langCode, { month: 'short', day: 'numeric', year: 'numeric' })}
          </DateLabel>
        </DateLabelContainer>
      </Row>
      <Row>
        <ProgressBarContainer>
          <ProgressBar>
            <ProgressFill
              progress={progress}
              backgroundColor={cardStatus === CardStatus.urgent ? theme.colors.statusDanger : theme.colors.primary}
            />
          </ProgressBar>
          <ProgressText>
            {modulesProgress} / {modulesTotal}
          </ProgressText>
        </ProgressBarContainer>
        <TrainingPrimaryButton
          disabled={!campaignUrl}
          onClick={() => {
            if (campaignUrl) {
              window.open(campaignUrl, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          {buttonText}
        </TrainingPrimaryButton>
      </Row>
    </MainTrainingCard>
  );
};
